'use client'

import { useEffect, useRef } from 'react'

/**
 * Cloudflare Turnstile on the public lead forms.
 *
 * Managed mode in `interaction-only` appearance: the widget stays invisible
 * for the ordinary visitor and only paints when Cloudflare decides a challenge
 * is warranted, so the forms keep their current shape. The token it produces
 * is short-lived and single-use, which is why `expired-callback` clears it and
 * why the parent bumps `resetSignal` after a rejected submit rather than
 * re-posting a spent one.
 *
 * With NEXT_PUBLIC_TURNSTILE_SITE_KEY unset (unit tests, a dev box that never
 * configured it) the component renders nothing and reports a null token. The
 * server half decides what to do with that: see
 * src/lib/security/turnstile.ts.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string | undefined
      remove: (id: string) => void
      reset: (id?: string) => void
    }
  }
}

const SCRIPT_ID = 'cf-turnstile-script'
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/** One in-flight load per page, shared by every widget on it. */
let scriptPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load')))
      return
    }
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Turnstile script failed to load'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

export interface TurnstileWidgetProps {
  /** Called with a fresh token, or null whenever the held token stops being valid. */
  onToken: (token: string | null) => void
  /** Bump this after a rejected submit to discard the spent token and draw a new challenge. */
  resetSignal?: number
  className?: string
}

export function TurnstileWidget({ onToken, resetSignal = 0, className }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  // The callbacks fire long after render, so read the current onToken through a
  // ref: the render effect then depends on the site key alone and never tears
  // the widget down because the parent handed it a new function identity.
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

  useEffect(() => {
    if (!siteKey) {
      onTokenRef.current(null)
      return
    }
    let cancelled = false

    loadTurnstileScript()
      .then(() => {
        if (cancelled || widgetIdRef.current || !containerRef.current || !window.turnstile) return
        widgetIdRef.current =
          window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            appearance: 'interaction-only',
            callback: (token: string) => onTokenRef.current(token),
            'expired-callback': () => onTokenRef.current(null),
            'timeout-callback': () => onTokenRef.current(null),
            'error-callback': () => onTokenRef.current(null),
          }) ?? null
      })
      .catch(() => onTokenRef.current(null))

    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [siteKey])

  useEffect(() => {
    if (!resetSignal) return
    onTokenRef.current(null)
    if (widgetIdRef.current && window.turnstile) window.turnstile.reset(widgetIdRef.current)
  }, [resetSignal])

  if (!siteKey) return null
  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />
}

export default TurnstileWidget
